import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Modal, Button, Card } from "react-bootstrap";
import DoughnutChart from "./AccountDetail";
import Spinner from "components/Spinner";
import RunoffInfoCard from "./RunoffInfoCard";
import CollapsibleCard from "./CollapsibleCard";
import AccrualDetailsCard from "./AccrualDetailCard";
import moment from "moment";

interface AccrualInfo {
    TotalHSABenefit: number;
    TotalLSABenefit: number;
    CurrentEligibleDate: string | null;
    NextAccrualDate: string | null;
    HSAMonthlyAmount: number;
    LSAMonthlyAmount: number;
    CyclesUntilFullyAccrued: number | null;
    accrualSchedule: any[];
    accrualPeriod: number;
}

interface HomeScreenState {
    loaded: boolean;
    plans: any[] | null;
    employee: any | null;
    showStartModal: boolean;
    showModal: boolean;
    modalType: string | null;
    modalBalance: number | string | null;
    modalStart: string | null;
    modalEnd: string | null;
    modalRunoff: string | null;
    modalCarry: string | null;
    company: any | null;
    hsaClaimCount: number;
    lsaClaimCount: number;
    accountType: string | null;
    policyNo: string | null;
    enableWellness: boolean;
    selectedItem: any | null;
    showPopup: boolean;
    hasAdditionalAccounts: boolean;
    isFlex: boolean;
    dynamicEmployeeStartDate: string | null;
    accrualInfo: AccrualInfo | null;
    hasAccrual: boolean;
    error: string | null;
    openInactiveEmpDateRanges: any[];
    closedInactiveDateRanges: any[];
}

export default class HomeScreen extends React.Component<any, HomeScreenState> {
  state: HomeScreenState = {
    loaded: false,
    plans: null,
    employee: null,
    showStartModal: false,
    showModal: false,
    modalType: null,
    modalBalance: null,
    modalStart: null,
    modalEnd: null,
    modalRunoff: null,
    modalCarry: null,
    company: null,
    hsaClaimCount: 0,
    lsaClaimCount: 0,
    accountType: null,
    policyNo: null,
    enableWellness: false,
    selectedItem: null,
    showPopup: false,
    hasAdditionalAccounts: false,
    isFlex: false,
    dynamicEmployeeStartDate: null,
    accrualInfo: null,
    hasAccrual: false,
    error: null,
    openInactiveEmpDateRanges: [],
    closedInactiveDateRanges: []
  };

  componentDidMount() {
    if (!this.state.loaded) {
      this.startLoading();
    }
  }

  handleItemSelect = (item) => {
    this.setState({
        selectedItem: item,
        showPopup: true
    });
  };

  closePopup = () => {
      this.setState({
          showPopup: false
      });
  };

  async startLoading() {
    const params = new URLSearchParams(window.location.search);
    const employeeId = params.get('employeeId');

    if (!employeeId) {
      console.error("No employeeId found in URL parameters.");
      this.setState({ loaded: true, error: "No employeeId found." });
      return;
    }

    try {
      const [homepageInfoResponse, accrualInfoResponse] = await Promise.all([
        axios.get(`/api/OpsEmployee/gethomepageinfo?employeeId=${employeeId}`),
        axios.get(`/api/OpsEmployee/getemployeeaccrualinfo?employeeId=${employeeId}`)
      ]);

      const homepageData = homepageInfoResponse.data;
      const employeeAccrualData = accrualInfoResponse.data;

      if (homepageData) {
        const newAccrualInfo = employeeAccrualData ? {
          TotalHSABenefit: employeeAccrualData.totalHSABenefit ?? 0,
          TotalLSABenefit: employeeAccrualData.totalLSABenefit ?? 0,
          CurrentEligibleDate: employeeAccrualData.currentEligibleDate ?? null,
          NextAccrualDate: employeeAccrualData.nextAccrualDate ?? null,
          HSAMonthlyAmount: employeeAccrualData.hsaMonthlyAmount ?? 0,
          LSAMonthlyAmount: employeeAccrualData.lsaMonthlyAmount ?? 0,
          CyclesUntilFullyAccrued: employeeAccrualData.cyclesUntilFullyAccrued ?? null,
          accrualSchedule: employeeAccrualData.accrualSchedule ?? [],
          accrualPeriod: employeeAccrualData.accrualPeriod ?? 0,
        } : null;

        this.setState({
          loaded: true,
          policyNo: homepageData.policyNo ?? '',
          plans: homepageData.spendingAccounts ?? [],
          isFlex: homepageData.hasFlex ?? false,
          employee: homepageData.employee ?? {},
          company: homepageData.company ?? {},
          lsaClaimCount: homepageData.lsaClaimCount ?? 0,
          hsaClaimCount: homepageData.hsaClaimCount ?? 0,
          enableWellness: homepageData.enableWellness ?? false,
          dynamicEmployeeStartDate: homepageData.dynamicEmployeeStartDate ?? '',
          hasAccrual: !!newAccrualInfo && (homepageData.hasAccrual ?? false),
          openInactiveEmpDateRanges: homepageData.openInactiveEmpDateRanges,
          closedInactiveDateRanges: homepageData.closedInactiveDateRanges,
          accrualInfo: newAccrualInfo,
          error: null,
        });

        if (!employeeAccrualData) {
             console.warn("Received empty or no data from getemployeeaccrualinfo API. Accrual features may not display.");
        }

      } else {
        console.warn("Received empty or no data from gethomepageinfo API.");
        this.setState({ loaded: true, error: "Failed to retrieve essential homepage data." });
      }

    } catch (error: any) {
      let errorMessage = "An unexpected error occurred during loading.";
      if (axios.isAxiosError(error)) {
        const apiErrorDetails = error.response?.data ? (typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data)) : '';
        errorMessage = `API Error: ${error.message}. ${apiErrorDetails}`;
        console.error("Axios error occurred:", error.message, error.response?.data || error.toJSON());
      } else {
        console.error("An unexpected error occurred:", error);
        errorMessage = `An unexpected error occurred: ${error.message || error}`;
      }
      this.setState({ loaded: true, error: errorMessage });
    }
  }
  
  renderContent() {
    
    if (!this.state.loaded) {
      return <Spinner />;
    }

    if (this.state.error) {
        return <div className="alert alert-danger mg-x-50" style={{ maxWidth: 1250 }}>Error loading page: {this.state.error}</div>;
    }

    if (!this.state.plans || !this.state.employee || !this.state.company || !this.state.dynamicEmployeeStartDate) {
        return <div className="alert alert-warning mg-x-50" style={{ maxWidth: 1250 }}>Required information is missing. Please contact support.</div>;
    }

    let {
      enableWellness,
      employee,
      company,
      modalCarry,
      plans,
      showModal,
      modalType,
      showStartModal,
      modalStart,
      modalEnd,
      modalBalance,
      modalRunoff,
      dynamicEmployeeStartDate,
      policyNo,
      isFlex,
      hasAccrual,
      accrualInfo
    } = this.state;
    
    let showStartPopup = (
      <a
        href="#"
        className="tx-14 mg-l-5"
        onClick={(e) => {
          e.preventDefault();
          this.setState({
            showStartModal: true,
          });
        }}
      >
        <i className="fa fa-info-circle tx-primary"></i>
      </a>
    );

    let startModal = (
      <Modal
        size="lg"
        style={{ color: "black" }}
        show={showStartModal}
        onHide={() => {
          this.setState({ showStartModal: false });
        }}
      >
        <Modal.Header style={{ borderBottom: "1px solid #adadad" }}>
          <Modal.Title>Benefit Start Date</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: "#E9ECEF" }}>
          <div
            className="card mg-t-20"
            style={{ boxShadow: "0 3px 8px rgba(0, 0, 0, 0.25)" }}
          >
            <div className="card-header tx-medium tx-uppercase bg-white">
              Description
            </div>
            <div className="card-body">
              <div className="mg-b-0">
                <p>
                  {this.state.dynamicEmployeeStartDate.replace(
                    "T00:00:00",
                    ""
                  )}
                </p>
                <p>
                  The date your company plan administrator has approved for your
                  benefits to begin.
                </p>
                <p>
                  You are able to claim expenses incurred after this date for
                  reimbursement if it falls within the current benefit year, or the previous year if you have expense carry forward.
                </p>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: "#E9ECEF" }}>
          <Button
            variant="primary"
            onClick={() => {
              this.setState({ showStartModal: false });
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );

    let accounts = null;
    if (plans) {
      accounts = plans.map((data, i) => {
      if (!data || !data.currYear) {
          console.warn("Skipping rendering account due to incomplete data:", data);
          return null;
      }
      let { type, label } = data;

      let accountName = "";

      if (type === "LSA") {
        accountName = enableWellness ? "Wellness Spending Account" : "Lifestyle Spending Account";
      } else if (label) {
        accountName = label;
      } else {
        accountName = "Health Spending Account";
      }

      const currYearCustomCategories = Array.isArray(data.currYearCustomCategories) ? data.currYearCustomCategories : [];
      const currentYearData = [
          {name: accountName, limit: data.currYear.limit ?? 0, balance: data.currYear.available ?? 0, backgroundColor: data.backgroundColor, textColor: data.textColor },
          ...currYearCustomCategories
      ];

      const totalLimit = currentYearData.reduce((acc, item) => acc + (item.limit ?? 0), 0).toFixed(2);
      const totalBalance = currentYearData.reduce((acc, item) => acc + (item.balance ?? 0), 0).toFixed(2);

      let totalPrevBalance = 0;
      let prevYearData = null;
      const prevYearCustomCategories = data.prevYear && Array.isArray(data.prevYearCustomCategories) ? data.prevYearCustomCategories : [];
      if (data.prevYear != null && data.prevYear.status == "OPEN") {
          prevYearData = [
              {name: accountName, limit: data.prevYear.limit ?? 0, balance: data.prevYear.available ?? 0 },
              ...prevYearCustomCategories
          ];
          totalPrevBalance = prevYearData.reduce((acc, item) => acc + (item.balance ?? 0), 0);
      }

      let boxColor = data.type === "LSA" ? "#059454" : "#1e85e5";

      let carryType = null;
      if (data.carryType === "CCF") {
        carryType = (
          <div className="text-left black">
            Your {accountName} has Credit Carry Forward enabled. After the run-off closes, 
            unused credits will be added to your new benefit year for a maximum of one year. 
            This sometimes can take a few weeks to process but you can still claim normally. 
            Our team will take the credits out of the correct benefit period and the correct account type. 
            Any credits under this section that are from two years ago will not be transferred.
            <br />
            <br />
            {data.hasAdditionalAccounts ? 
              <>Note: Credit Carry Forward applies only to your standard {accountName}, 
              and does not apply to enhanced benefits. These enhanced benefits 
              renew annually and do not carry forward.
              </> : <></>}
          </div>
        );
      } else if (data.carryType === "ECF") {
        carryType = (
          <div className="text-left black">
            Your {accountName} has Expense Carry Forward enabled. After run-off closes, 
            expenses dated from the previous year are still eligible in the current year, 
            and paid out of current year funds.
            <br />
            <br />
            {data.hasAdditionalAccounts ?
              <>Note: Expense Carry Forward applies only to your standard {accountName}, 
              and does not apply to enhanced benefits. These enhanced benefits can 
              be claimed only for expenses dated within their respective benefit year.
              </>:<></>}
          </div>
        );
      } else {
        carryType = (
          <div className="text-left black">
            Your {accountName} has no Carry-Forward. After the run-off closes,
            unused credits will not transfer over to your new benefit year.
          </div>
        );
      }

      const { openInactiveEmpDateRanges, closedInactiveDateRanges } = this.state;
      let inactiveClaimMessage = null;

      if (openInactiveEmpDateRanges && openInactiveEmpDateRanges.length > 0) {
        const openRange = openInactiveEmpDateRanges[0];
        inactiveClaimMessage = (
          <strong style={{ color: "Black", display: "block", marginBottom: "1rem" }}>
            An Inactive period has been applied, expenses dated {moment(openRange.fromDate).format("MMMM D, YYYY")}{" "}
            or later are not eligible to claim. Please contact your Plan Administrator if you have questions.
          </strong>
        );
      } else if (closedInactiveDateRanges && closedInactiveDateRanges.length > 0) {
        const closedRange = closedInactiveDateRanges[0];
        inactiveClaimMessage = (
          <strong style={{ color: "Black", display: "block", marginBottom: "1rem" }}>
            An Inactive period has been applied, expenses dated on or between {moment(closedRange.fromDate).format("MMMM D, YYYY")}{" "}
            and {moment(closedRange.toDate).format("MMMM D, YYYY")} are not eligible to claim. Please contact your Plan Administrator 
            if you have questions.
          </strong>
        );
      }

      return (
          <React.Fragment key={data.type || i}>
            <div 
              className="row pd-t-30"
              key={data.type || i}
            >
                <div className="col-md-4 col-lg-4">
                  <div style={{display:"flex", flexDirection:"column", alignItems: "center"}}>
                      <DoughnutChart id={`doughnut-chart-${data.type}`} data={currentYearData} />
                  </div>
                </div>

                {(data.prevYear?.status === 'OPEN' || (hasAccrual && accrualInfo)) && (
                <div className="col-md-4 col-lg-4 pd-y-20 pd-t-md-30">

                    {data.prevYear?.status === 'OPEN' && prevYearData && (
                    <RunoffInfoCard
                        prevYearData={prevYearData}
                        boxColor={boxColor}
                        startDate={data.prevYear.startDate}
                        endDate={data.prevYear.endDate}
                        runoff={data.prevYear.runoff}
                    />)}

                    {hasAccrual && accrualInfo && (
                    <React.Fragment>
                         <CollapsibleCard
                              title={"Your "+accountName+" Schedule"}
                              eventKey={`${data.type}-accrual-details`}
                              defaultOpen
                              bgColor={boxColor}
                          >
                            <AccrualDetailsCard
                                totalYearlyBenefit={data.type === 'HSA' ? accrualInfo.TotalHSABenefit : accrualInfo.TotalLSABenefit}
                                accrualFrequency={
                                    accrualInfo.accrualPeriod === 12 ? 'monthly'
                                    : accrualInfo.accrualPeriod === 4 ? 'quarterly'
                                    : accrualInfo.accrualPeriod === 2 ? 'semiannually'
                                    : accrualInfo.accrualPeriod === 1 ? 'annually'
                                    : 'Per Period'
                                }
                                amountPerPeriod={data.type === 'HSA' ? accrualInfo.HSAMonthlyAmount : accrualInfo.LSAMonthlyAmount}
                                accrualSchedule={accrualInfo.accrualSchedule}
                                type={data.type}
                                currentEligibleDate={accrualInfo.CurrentEligibleDate}
                                nextAccrualDate={accrualInfo.NextAccrualDate}
                            />
                        </CollapsibleCard>
                    </React.Fragment>
                    )}
                </div>
                )}

                <div className="col-md-4 col-lg-4 col-custom">
                <div className={`text-center ${ (data.prevYear?.status === 'OPEN' || (hasAccrual && accrualInfo)) ? '' : 'mg-t-80' }`}>
                    {(parseFloat(totalLimit) > 0 || (parseFloat(totalBalance) + totalPrevBalance) > 0) ? (
                    <div className="text-left pd-t-10">
                      <Link                          
                         className={"btn btn-primary pd-x-25-force home-camera-" + data.type.toLowerCase()}
                         to={`/ops/employee/account?employeeId=${employee?.id}&type=${data.type}`}
                         style={{width: "300px"}}
                      >
                        Make a {accountName} Claim
                      </Link>
                    </div>) : <div className="pd-t-10 text-muted">No available balance or limit to claim against.</div>}

                    <div className="text-left pd-t-10">
                     <Link                          
                         className={`btn btn-primary-green pd-x-25-force home-camera-inv-${data.type.toLowerCase()}`}
                         to={`/ops/employee/eligibleexpenses?employeeId=${employee?.id}&type=${data.type}`}
                         style={{width: "300px"}}
                     >
                         {currentYearData.length > 1 ? "Your Accounts" : "Your Account"}
                     </Link>
                    </div>
                </div>
                </div>
            </div>

            <div
                className="row"
                style={{ borderBottom: "1px solid #dee2e6" }}
            >
                <div className="col-md-4 col-lg-4 pd-b-15">
                <div className="text-center">
                    <div style={{ margin: "auto", textAlign: "center" }}>
                    <div className="h6 black pd-t-10">{accountName}</div>
                    <div className="h6 black">
                        Current year limit: ${totalLimit}
                        <a
                            href="#"
                            className="tx-14 mg-l-5"
                            onClick={(e) => {
                                e.preventDefault();
                                let type = data.type === "LSA" && enableWellness ? "WSA" : data.type;
                                this.setState({
                                showModal: true,
                                modalType: "Current Year " + type,
                                modalBalance: data.currYear.limit?.toFixed(2) ?? '0.00',
                                modalStart: data.currYear.startDate,
                                modalEnd: data.currYear.endDate,
                                modalRunoff: data.currYear.runoff,
                                modalCarry: data.carryType,
                                });
                            }}
                        >
                        <i className="fa fa-info-circle tx-primary"></i>
                        </a>
                    </div>
                    {data.type && employee?.id && (
                         <div className="h6 black pd-b-40">
                              <Link style={{"color":"#0d6efd"}} to={`/ops/employee/limitcalculation?employeeId=${employee.id}&type=${data.type}`}>
                                  Click here to see how your {enableWellness && data.type === "LSA" ? "WSA" : data.type} limit is calculated
                              </Link>
                          </div>
                    )}

                    </div>
                </div>
                </div>
                
                <div 
                  className="col-sm-1 col-md-8 col-lg-8 pd-b-40"
                  style={{
                    border: "2px solid #007bff",
                    borderRadius: "8px",
                    padding: "15px",
                    backgroundColor: "#e9f5ff",
                    marginBottom: "20px",
                }}>
                  {carryType}
                  {inactiveClaimMessage}
                </div>

            </div>
            </React.Fragment>
        );
        });
    }

    let carryTypeText = null;
    let carryText = null;
    if (modalCarry == "CCF") {
      carryTypeText = "Credit Carry Forward";
      carryText = (
        <>
          <p>
            Unused credits will be added to your new benefit year for a maximum
            of one year. This sometimes can take a few weeks to process but you
            can still claim normally. Our team will take the credits out of the
            correct benefit period.
          </p>
          <p>
            Note: Any credits under this section that are from two years ago
            will not be transferred.
          </p>
        </>
      );
    } else if (modalCarry == "ECF") {
      carryTypeText = "Expense Carry Forward";
      carryText = (
        <p>
          Unused credits will not be transfered over to your new benefit year.
          However, you have a longer period to claim receipts from your previous
          year using new benefit year credits
        </p>
      );
    } else {
        carryTypeText = "No Carry Forward";
         carryText = (
            <p>
                Unused credits will not transfer over to your new benefit year after the run-off period ends.
            </p>
        );
    }

    let infoModal = (
      <Modal
        size="lg"
        style={{ color: "black" }}
        show={showModal}
        onHide={() => {
          this.setState({ showModal: false });
        }}
      >
        <Modal.Header style={{ borderBottom: "1px solid #adadad" }}>
          <Modal.Title>{modalType}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: "#E9ECEF" }}>
          <p>
            Benefit period ({modalStart} - {modalEnd})
          </p>
          <div
            className="card mg-t-20"
            style={{ boxShadow: "0 3px 8px rgba(0, 0, 0, 0.25)" }}
          >
            <div className="card-header tx-medium tx-uppercase bg-white">
              Yearly Limit
            </div>
            <div className="card-body">
              <div className="mg-b-0">
                <p>${modalBalance}</p>
              </div>
            </div>
          </div>
          <div
            className="card mg-t-20"
            style={{ boxShadow: "0 3px 8px rgba(0, 0, 0, 0.25)" }}
          >
            <div className="card-header tx-medium tx-uppercase bg-white">
              Runoff Description
            </div>
            <div className="card-body">
              <div className="mg-b-0">
                <p>{modalRunoff} at 11:59 MT</p>
                <p>
                  The deadline to submit expenses incurred within your previous
                  benefit year. All proper documentation must be in before this
                  deadline.
                </p>
                <p>
                  We recommend claiming early, so you have time to resubmit if
                  needed as there are no extensions once the run-off has ended.
                </p>
              </div>
            </div>
          </div>
          {carryTypeText && carryText && (
              <div
                className="card mg-t-20"
                style={{ boxShadow: "0 3px 8px rgba(0, 0, 0, 0.25)" }}
              >
                <div>
                    <div className="card-header tx-medium tx-uppercase bg-white">
                      Account Carry Forward ({carryTypeText})
                    </div>
                    <div className="card-body">
                    <div className="mg-b-0">{carryText}</div>
                    </div>
                </div>
              </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: "#E9ECEF" }}>
          <Button
            variant="primary"
            onClick={() => {
              this.setState({ showModal: false });
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );

    return (
      <div>
        <Card
          className="mg-x-50 mg-b-30"
          style={{
            maxWidth: 1250,
            fontSize: 17,
            padding:20,
            fontWeight: "bold",
            marginTop: 20,
            border: "2px solid #007bff",
            borderRadius: "8px",
            backgroundColor: "#e9f5ff",
            width: "100%",
            marginBottom: "20px",
          }}>
          {plans && plans[0]?.currYear && (
             <div style={{marginBottom:10}}>
                The current benefit year goes from{" "}
                <strong>{plans[0].currYear.startDate}</strong> to{" "}
                <strong>{plans[0].currYear.endDate}</strong>. The run-off deadline for the
                current benefit year is <strong>{plans[0].currYear.runoff}</strong> at
                11:59 PM MT.
             </div>
          )}
           {dynamicEmployeeStartDate && (
                <div style={{marginBottom:10}}>
                    Your benefit start date is{" "}
                    <strong>{dynamicEmployeeStartDate.replace("T00:00:00", "")}</strong>.{" "}
                    {showStartPopup}
                </div>
           )}

          {policyNo && (
                <div>
                    Under this policy (Policy# <strong>{policyNo}</strong>), you have the
                    following coverage{isFlex && " as a Flex Spending Account"}:
                </div>
          )}

        </Card>

        <div
          className="br-section-wrapper mg-b-30 mg-x-50"
          style={{ maxWidth: 1250, padding: 0, marginTop: 20 }}
        >
          <Card className="mg-t-0">
            <Card.Header
              className="tx-medium tx-black"
              style={{ backgroundColor: "#fff" }}
            >
              {company?.name?.toUpperCase() ?? 'COMPANY'} SPENDING ACCOUNT
            </Card.Header>
            <Card.Body>
              <div>{accounts}</div>
            </Card.Body>
          </Card>
        </div>

        {infoModal}
        {startModal}
      </div>
    );
  }

  render() {
    return <>{this.renderContent()}</>;
  }
}